import React from "react";
import { motion } from "framer-motion";
import {
	FileText,
	CheckCircle2,
	AlertTriangle,
	Target,
	TrendingUp,
	Sparkles,
} from "lucide-react";

const KPI_CARD_CONFIG = [
	{
		icon: FileText,
		label: "Pages Analyzed",
		color: "blue",
		prop: "totalPages",
	},
	{
		icon: Target,
		label: "Core Pages Found",
		color: "purple",
		prop: "corePages",
	},
	{
		icon: AlertTriangle,
		label: "Issues Identified",
		color: "orange",
		prop: "issueCount",
	},
	{
		icon: CheckCircle2,
		label: "Recommendations",
		color: "green",
		prop: "recommendationCount",
	},
];

export default function KPICards({
	totalPages = 0,
	corePages = 0,
	issueCount = 0,
	recommendationCount = 0,
	topStrengths = [],
	topWeaknesses = [],
}) {
	const values = { totalPages, corePages, issueCount, recommendationCount };

	return (
		<div className="kpi-cards">
			<div className="kpi-cards__grid">
				{KPI_CARD_CONFIG.map((card, index) => {
					const Icon = card.icon;
					return (
						<motion.div
							key={card.label}
							className={`kpi-card kpi-card--${card.color}`}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4, delay: index * 0.1 }}
						>
							<div className="kpi-card__body">
								<div className="kpi-card__content">
									<p className="kpi-card__label">{card.label}</p>
									<motion.p
										className="kpi-card__value"
										initial={{ scale: 0 }}
										animate={{ scale: 1 }}
										transition={{
											type: "spring",
											stiffness: 200,
											delay: index * 0.1 + 0.2,
										}}
									>
										{values[card.prop]}
									</motion.p>
								</div>
								<Icon className="kpi-card__icon" />
							</div>
						</motion.div>
					);
				})}
			</div>

			{(topStrengths.length > 0 || topWeaknesses.length > 0) && (
				<div className="kpi-cards__insights">
					{topStrengths.length > 0 && (
						<motion.div
							className="kpi-cards__strengths"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5, delay: 0.5 }}
						>
							<div className="kpi-cards__insights-header">
								<TrendingUp className="kpi-cards__insights-icon kpi-cards__insights-icon--strength" />
								<h3 className="kpi-cards__insights-title">Top Strengths</h3>
							</div>
							<ul className="kpi-cards__insights-list">
								{topStrengths.slice(0, 3).map((strength, index) => (
									<motion.li
										key={index}
										className="kpi-cards__insights-item"
										initial={{ opacity: 0, x: -10 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: 0.6 + index * 0.1 }}
									>
										<CheckCircle2 className="kpi-cards__insights-item-icon kpi-cards__insights-item-icon--strength" />
										<span>{strength}</span>
									</motion.li>
								))}
							</ul>
						</motion.div>
					)}

					{topWeaknesses.length > 0 && (
						<motion.div
							className="kpi-cards__weaknesses"
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5, delay: 0.5 }}
						>
							<div className="kpi-cards__insights-header">
								<AlertTriangle className="kpi-cards__insights-icon kpi-cards__insights-icon--weakness" />
								<h3 className="kpi-cards__insights-title">Areas for Improvement</h3>
							</div>
							<ul className="kpi-cards__insights-list">
								{topWeaknesses.slice(0, 3).map((weakness, index) => (
									<motion.li
										key={index}
										className="kpi-cards__insights-item"
										initial={{ opacity: 0, x: 10 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: 0.6 + index * 0.1 }}
									>
										<Sparkles className="kpi-cards__insights-item-icon kpi-cards__insights-item-icon--weakness" />
										<span>{weakness}</span>
									</motion.li>
								))}
							</ul>
						</motion.div>
					)}
				</div>
			)}
		</div>
	);
}
